import { useTitle } from "@/hooks/use-title.hook.ts";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { EmailIntegrations } from "@/pages/me/components/EmailIntegrations.tsx";
import { McpConnection } from "@/pages/me/components/McpConnection.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  useEmployeeMe,
  useEmployeeUpdateAccountSettings,
} from "@/queries/employees/use-employees.query.ts";
import { getMcpServerUrl } from "@/helpers/mcp.helper.ts";
import { sendDisplayTypeToExtension } from "@/helpers/send-to-extension.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiExtensionDisplayTypes,
  getExtensionDisplayTypeSelectItems,
} from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ButtonIcon,
  FontAwesomeIcon,
  Page,
  Select,
  Stack,
  Title,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const AccountSettingsIntegrations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: employee } = useEmployeeMe();
  const { canIEdit } = usePermissions();
  const { showInfo } = useToasts();
  const updateAccountSettings = useEmployeeUpdateAccountSettings();
  const savedDisplayType = employee?.extension_display_type;
  const [displayType, setDisplayType] = useState<ApiExtensionDisplayTypes>(
    savedDisplayType ?? ApiExtensionDisplayTypes.sidePanel
  );
  const [syncedDisplayType, setSyncedDisplayType] = useState(savedDisplayType);

  if (savedDisplayType !== syncedDisplayType) {
    setSyncedDisplayType(savedDisplayType);
    setDisplayType(savedDisplayType ?? ApiExtensionDisplayTypes.sidePanel);
  }

  useTitle(t("core:settings_recruitment"));

  const mcpServerUrl = getMcpServerUrl();

  const canEditExtensionDisplayType = canIEdit(ApiPermission.recruiting_module);
  const handleDisplayTypeChange = (nextDisplayType: ApiExtensionDisplayTypes) => {
    if (nextDisplayType === displayType || updateAccountSettings.isPending) return;

    const previousDisplayType = displayType;
    setDisplayType(nextDisplayType);
    updateAccountSettings.mutate(
      { extension_display_type: nextDisplayType },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.employees.accountSettingsEdited);
          sendDisplayTypeToExtension(nextDisplayType);
          showInfo({ title: t("common:changes_saved_successfully") });
        },
        onError: () => setDisplayType(previousDisplayType),
      }
    );
  };

  return (
    <Page>
      <Page.Header
        backSlot={
          <ButtonIcon
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-arrow-left"} />}
            onClick={() => navigate("/account-settings")}
          />
        }
        title={t("core:settings_recruitment")}
      />
      <Page.Content>
        <Stack className={"container-sm mx-auto"} gap={"sm"}>
          <DemoUnavailable appearance="notice" />
          <Stack gap={"2xl"} className={"card-panel-sm"}>
            <Title
              header={t("core:extension_appearance_title")}
              caption={t("core:extension_appearance_caption")}
            />

            {canEditExtensionDisplayType && (
              <Select
                label={t("core:extension_style")}
                options={getExtensionDisplayTypeSelectItems()}
                emptyTitle={t("common:select")}
                value={displayType}
                isLoading={updateAccountSettings.isPending}
                onChange={(value) => {
                  if (value) handleDisplayTypeChange(value as ApiExtensionDisplayTypes);
                }}
              />
            )}
          </Stack>

          <EmailIntegrations />

          {mcpServerUrl && <McpConnection url={mcpServerUrl} />}
        </Stack>
      </Page.Content>
    </Page>
  );
};
